// app/javascript/controllers/share-form_controller.js
import { Controller } from "@hotwired/stimulus";
import JustValidate from "just-validate";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
    static targets = ["template", "fields", "form", "submit"];

    connect() {
        this.initValidation();
    }

    /**
     * Called when the "Add more" button is clicked.
     */
    addRow(event) {
        event.preventDefault();
        const content = this.templateTarget.innerHTML;
        this.fieldsTarget.insertAdjacentHTML("beforeend", content);
        // A microtask ensures the new fields are in the DOM before we re-initialize.
        queueMicrotask(() => this.initValidation());
    }

    /**
     * Sets up or resets the JustValidate instance.
     * This is called on connect and after adding new rows.
     */
    initValidation() {

        // Destroy the old instance to avoid orphaned listeners
        if (this.validator) {
            this.validator.destroy();
        }

        this.validator = new JustValidate(this.formTarget, {
            // We take full manual control.
            validateBeforeSubmitting: false,
            focusInvalidField: true,
            errorFieldCssClass: "is-invalid",
        });

        // Add rules to all currently visible fields.
        this._addValidationRules();

        // This is the only way the form will submit.
        this.validator.onSuccess(() => {
            this._submitWithTurbo(this.formTarget);
        });
    }

    /**
     * This action is called directly by a data-action on the form's submit event.
     * It prevents the default submission and hands control to the validator.
     */
    async handleSubmit(event) {
        event.preventDefault();
        await this.validator.validate();
    }

    /**
     * Manually performs a fetch request that mimics a Turbo Stream submission.
     * This is called by the JustValidate `.onSuccess` callback.
     */
    async _submitWithTurbo(form) {
        this.submitTarget.disabled = true;
        this.submitTarget.textContent = "Sending…";

        try {
            const formData = new FormData(form);
            const response = await fetch(form.action, {
                method: form.method,
                headers: {
                    "Accept": "text/vnd.turbo-stream.html",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Submission failed: ${response.statusText}`);
            }

            const responseText = await response.text();
            Turbo.renderStreamMessage(responseText);

            // The form is re-rendered on success by your backend,
            // so we re-initialize validation for the new, empty fields.
            this.initValidation();

        } catch (error) {
            console.error("Share form submission failed:", error);
            this.submitTarget.disabled = false;
            this.submitTarget.textContent = "Share with love";
        }
    }

    _addValidationRules() {
        // This helper keeps the rule definitions clean.
        this.formTarget.querySelectorAll(".first-name").forEach((el) => {
            this.validator.addField(el, [{ rule: "required", errorMessage: "First name is required" }]);
        });
        this.formTarget.querySelectorAll(".last-name").forEach((el) => {
            this.validator.addField(el, [{ rule: "required", errorMessage: "Last name is required" }]);
        });
        this.formTarget.querySelectorAll(".email").forEach((el) => {
            this.validator.addField(el, [
                { rule: "required", errorMessage: "Email is required" },
                { rule: "email", errorMessage: "Enter a valid email" },
            ]);
        });
    }
}